/**
 * Sign Line Detection Utility
 * Groups signs that are on the same road edge for auto-arrow generation
 */

import type { PlacedSign, SignEdgeMetadata, LatLng } from '../types/signs';
import type { SignLine, SignLineDetectionConfig, DEFAULT_SIGN_LINE_CONFIG } from '../types/signLines';

/**
 * Haversine distance calculation between two points (in meters)
 */
export const haversineDistance = (
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number => {
  const R = 6371000; // Earth radius in meters
  const toRadians = (deg: number) => (deg * Math.PI) / 180;
  
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Calculate angular difference between two angles (in degrees)
 * Returns value between 0 and 180
 */
export const angularDifference = (angle1: number, angle2: number): number => {
  let diff = Math.abs(angle1 - angle2) % 360;
  if (diff > 180) {
    diff = 360 - diff;
  }
  return diff;
};

/**
 * Check if two signs are potentially on the same line
 */
export const areSignsCompatible = (
  sign1: PlacedSign,
  sign2: PlacedSign,
  config: SignLineDetectionConfig
): boolean => {
  // Both signs must have edge metadata with valid road segment ID
  if (!sign1.edgeMetadata || !sign2.edgeMetadata) {
    return false;
  }

  const meta1 = sign1.edgeMetadata;
  const meta2 = sign2.edgeMetadata;

  // Must have valid road segment IDs
  if (!meta1.roadSegmentId || !meta2.roadSegmentId) {
    return false;
  }

  // Must be on the same side of the road
  if (meta1.side !== meta2.side) {
    return false;
  }

  // CRITICAL: Even if on "same side", check that edge angles are similar
  // Signs on opposite sides of a road would have ~180° different angles
  // This catches cases where side detection failed but angles reveal the truth
  const angleDiff = angularDifference(meta1.edgeAngle, meta2.edgeAngle);
  if (angleDiff > config.maxAngularDifference) {
    // Angles are too different - signs are likely on opposite edges
    return false;
  }

  // Check if road segment IDs match (same road segment)
  // We already verified angles are similar above, so this is safe
  const sameSegment = meta1.roadSegmentId === meta2.roadSegmentId;

  // If not same segment, angles must be similar (already checked above)
  // If same segment, we still require similar angles to avoid opposite-side grouping
  if (!sameSegment) {
    return false; // For now, require same road segment for auto-arrows
  }

  // Same road segment, same side, similar angle => compatible
  return true;
};

/**
 * Detect sign lines from a list of placed signs
 * Groups signs that are on the same road edge
 */
export const detectSignLines = (
  signs: PlacedSign[],
  config: SignLineDetectionConfig = {
    maxSignDistance: 500, // retained for config compatibility (not used in compatibility check)
    maxAngularDifference: 15,
    minSignsForLine: 2,
  }
): SignLine[] => {
  // Filter signs with edge metadata
  const signsWithMetadata = signs.filter(s => s.edgeMetadata && s.edgeMetadata.roadSegmentId);
  
  console.log('[SignLineDetection] Signs with metadata:', signsWithMetadata.length);
  
  if (signsWithMetadata.length < config.minSignsForLine) {
    console.log('[SignLineDetection] Not enough signs with metadata');
    return [];
  }
  
  // Debug: Check compatibility between each pair
  for (let i = 0; i < signsWithMetadata.length; i++) {
    for (let j = i + 1; j < signsWithMetadata.length; j++) {
      const s1 = signsWithMetadata[i];
      const s2 = signsWithMetadata[j];
      const distance = haversineDistance(s1.lat, s1.lng, s2.lat, s2.lng);
      const compatible = areSignsCompatible(s1, s2, config);
      console.log(`[SignLineDetection] Checking ${s1.id.substring(0, 20)} <-> ${s2.id.substring(0, 20)}:`);
      console.log(`  - Distance: ${distance.toFixed(1)}m (max: ${config.maxSignDistance}m)`);
      console.log(`  - Same side: ${s1.edgeMetadata?.side === s2.edgeMetadata?.side}`);
      console.log(`  - Same road: ${s1.edgeMetadata?.roadSegmentId === s2.edgeMetadata?.roadSegmentId}`);
      console.log(`  - Angle diff: ${angularDifference(s1.edgeMetadata?.edgeAngle || 0, s2.edgeMetadata?.edgeAngle || 0)}°`);
      console.log(`  - Compatible: ${compatible}`);
    }
  }

  // Use union-find to group compatible signs
  const parent: Map<string, string> = new Map();
  
  const find = (id: string): string => {
    if (!parent.has(id)) {
      parent.set(id, id);
      return id;
    }
    if (parent.get(id) !== id) {
      parent.set(id, find(parent.get(id)!));
    }
    return parent.get(id)!;
  };
  
  const union = (id1: string, id2: string): void => {
    const root1 = find(id1);
    const root2 = find(id2);
    if (root1 !== root2) {
      parent.set(root1, root2);
    }
  };

  // Initialize all signs
  signsWithMetadata.forEach(sign => find(sign.id));

  // Group compatible signs
  for (let i = 0; i < signsWithMetadata.length; i++) {
    for (let j = i + 1; j < signsWithMetadata.length; j++) {
      if (areSignsCompatible(signsWithMetadata[i], signsWithMetadata[j], config)) {
        union(signsWithMetadata[i].id, signsWithMetadata[j].id);
      }
    }
  }

  // Collect groups
  const groups: Map<string, PlacedSign[]> = new Map();
  signsWithMetadata.forEach(sign => {
    const root = find(sign.id);
    if (!groups.has(root)) {
      groups.set(root, []);
    }
    groups.get(root)!.push(sign);
  });

  // Convert groups to SignLines
  const signLines: SignLine[] = [];
  let lineIndex = 0;

  groups.forEach((groupSigns) => {
    if (groupSigns.length < config.minSignsForLine) {
      return;
    }

    // Sort signs by position along edge
    const sortedSigns = [...groupSigns].sort((a, b) => {
      const posA = a.edgeMetadata?.positionAlongEdge ?? 0;
      const posB = b.edgeMetadata?.positionAlongEdge ?? 0;
      return posA - posB;
    });

    // Get metadata from first sign (they should all be similar)
    const firstMeta = sortedSigns[0].edgeMetadata!;

    // Calculate aligned path (straight line through all signs)
    const alignedPath = calculateAlignedPath(sortedSigns);

    const signLine: SignLine = {
      id: `sign-line-${lineIndex++}-${Date.now()}`,
      signIds: sortedSigns.map(s => s.id),
      roadSegmentId: firstMeta.roadSegmentId,
      side: firstMeta.side,
      alignedPath,
      autoArrowsEnabled: true,
    };

    signLines.push(signLine);
  });

  return signLines;
};

/**
 * Calculate a straight line path that best fits the given signs
 * Uses linear regression for the best-fit line
 */
export const calculateAlignedPath = (signs: PlacedSign[]): LatLng[] => {
  if (signs.length < 2) {
    return signs.map(s => ({ lat: s.lat, lng: s.lng }));
  }

  // For 2 signs, just return the direct line
  if (signs.length === 2) {
    return [
      { lat: signs[0].lat, lng: signs[0].lng },
      { lat: signs[1].lat, lng: signs[1].lng },
    ];
  }

  // For 3+ signs, calculate best-fit line using linear regression
  const n = signs.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

  signs.forEach(sign => {
    sumX += sign.lng;
    sumY += sign.lat;
    sumXY += sign.lng * sign.lat;
    sumX2 += sign.lng * sign.lng;
  });

  const meanX = sumX / n;
  const meanY = sumY / n;

  // Calculate slope and intercept
  const denominator = sumX2 - n * meanX * meanX;
  
  if (Math.abs(denominator) < 1e-10) {
    // Vertical line - use mean X
    const minLat = Math.min(...signs.map(s => s.lat));
    const maxLat = Math.max(...signs.map(s => s.lat));
    return [
      { lat: minLat, lng: meanX },
      { lat: maxLat, lng: meanX },
    ];
  }

  const slope = (sumXY - n * meanX * meanY) / denominator;
  const intercept = meanY - slope * meanX;

  // Project each sign onto the best-fit line
  // Line equation: lat = slope * lng + intercept
  // For a point (lng0, lat0), the projection is:
  // lng_proj = (lng0 + slope * (lat0 - intercept)) / (1 + slope^2)
  // lat_proj = slope * lng_proj + intercept

  const projectedPoints: LatLng[] = signs.map(sign => {
    const lng_proj = (sign.lng + slope * (sign.lat - intercept)) / (1 + slope * slope);
    const lat_proj = slope * lng_proj + intercept;
    return { lat: lat_proj, lng: lng_proj };
  });

  // Sort by distance along the line
  projectedPoints.sort((a, b) => {
    // Use lng as the primary sorting dimension (could also use lat depending on line orientation)
    if (Math.abs(slope) > 1) {
      return a.lat - b.lat;
    }
    return a.lng - b.lng;
  });

  return projectedPoints;
};

/**
 * Find the sign line that a sign belongs to
 */
export const findSignLineForSign = (
  signId: string,
  signLines: SignLine[]
): SignLine | undefined => {
  return signLines.find(line => line.signIds.includes(signId));
};

/**
 * Get signs that are adjacent to a given sign in a sign line
 */
export const getAdjacentSigns = (
  signId: string,
  signLine: SignLine,
  allSigns: PlacedSign[]
): { previous?: PlacedSign; next?: PlacedSign } => {
  const index = signLine.signIds.indexOf(signId);
  if (index === -1) {
    return {};
  }

  const signMap = new Map(allSigns.map(s => [s.id, s]));

  return {
    previous: index > 0 ? signMap.get(signLine.signIds[index - 1]) : undefined,
    next: index < signLine.signIds.length - 1 ? signMap.get(signLine.signIds[index + 1]) : undefined,
  };
};
